import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useToast } from '../../contexts/ToastContext';

const ProfitAnalyticsDashboard = () => {
  const { info } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedChart, setSelectedChart] = useState('profit-trends');

  // Mock data for profit analytics
  const profitMetrics = [
    {
      title: "Lucro Total (Mês)",
      value: "R$ 4.235,80",
      trend: "up",
      trendValue: "+32,8%",
      icon: "TrendingUp",
      color: "success",
      description: "Lucro líquido após custos"
    },
    {
      title: "Receita Bruta",
      value: "R$ 8.647,25",
      trend: "up",
      trendValue: "+18,5%",
      icon: "DollarSign",
      color: "primary",
      description: "Faturamento total"
    },
    {
      title: "Margem de Lucro",
      value: "49,2%",
      trend: "up",
      trendValue: "+5,3%",
      icon: "Percent",
      color: "warning",
      description: "Margem líquida média"
    },
    {
      title: "ROI Médio",
      value: "267%",
      trend: "up",
      trendValue: "+15,7%",
      icon: "Target",
      color: "secondary",
      description: "Retorno sobre investimento"
    }
  ];

  // Mock profit trends data
  const profitTrends = [
    { month: 'Jan', lucro: 2150, receita: 4200, custos: 2050, margem: 51.2 },
    { month: 'Fev', lucro: 2890, receita: 5500, custos: 2610, margem: 52.5 },
    { month: 'Mar', lucro: 3240, receita: 6100, custos: 2860, margem: 53.1 },
    { month: 'Abr', lucro: 3650, receita: 6800, custos: 3150, margem: 53.7 },
    { month: 'Mai', lucro: 3980, receita: 7350, custos: 3370, margem: 54.1 },
    { month: 'Jun', lucro: 4235, receita: 8647, custos: 4412, margem: 49.2 }
  ];

  // Mock cost breakdown
  const costBreakdown = [
    { name: 'Material', value: 2847, percentage: 32.9, color: '#3b82f6' },
    { name: 'Energia', value: 485, percentage: 5.6, color: '#10b981' },
    { name: 'Depreciação', value: 320, percentage: 3.7, color: '#f59e0b' },
    { name: 'Mão de Obra', value: 760, percentage: 8.8, color: '#ef4444' },
    { name: 'Lucro', value: 4235, percentage: 49.0, color: '#8b5cf6' }
  ];

  // Mock project profitability
  const topProjects = [
    {
      id: 1,
      name: "Miniatura D&D - Dragão",
      revenue: "R$ 285,00",
      cost: "R$ 95,50",
      profit: "R$ 189,50",
      margin: "66.5%",
      quantity: 3,
      material: "Resina",
      printTime: "18h"
    },
    {
      id: 2,
      name: "Peça Automotiva - Tampa",
      revenue: "R$ 450,00",
      cost: "R$ 167,20",
      profit: "R$ 282,80",
      margin: "62.8%",
      quantity: 5,
      material: "PETG",
      printTime: "12h"
    },
    {
      id: 3,
      name: "Utensílio Cozinha - Suporte",
      revenue: "R$ 180,00",
      cost: "R$ 73,40",
      profit: "R$ 106,60",
      margin: "59.2%",
      quantity: 8,
      material: "PLA",
      printTime: "6h"
    },
    {
      id: 4,
      name: "Decorativo - Vaso Geométrico",
      revenue: "R$ 320,00",
      cost: "R$ 142,80",
      profit: "R$ 177,20",
      margin: "55.4%",
      quantity: 4,
      material: "PLA",
      printTime: "15h"
    }
  ];

  // Mock material efficiency
  const materialEfficiency = [
    { material: 'PLA', usage: 2.4, cost: 89.90, profit: 1240, efficiency: 92.5 },
    { material: 'PETG', usage: 1.8, cost: 125.00, profit: 890, efficiency: 88.3 },
    { material: 'ABS', usage: 1.2, cost: 95.50, profit: 650, efficiency: 85.7 },
    { material: 'Resina', usage: 0.8, cost: 185.00, profit: 480, efficiency: 94.1 },
    { material: 'TPU', usage: 0.5, cost: 165.00, profit: 320, efficiency: 81.2 }
  ];

  // Mock customer insights
  const customerInsights = [
    { segment: 'Hobbyistas', revenue: 3200, profit: 1580, count: 45 },
    { segment: 'Empresas', revenue: 2850, profit: 1420, count: 12 },
    { segment: 'Protótipos', revenue: 1890, profit: 890, count: 23 },
    { segment: 'Educação', revenue: 707, profit: 345, count: 18 }
  ];

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleExportData = (type) => {
    // Mock export functionality
    info(`Exportando dados de ${type}...`);
  };

  const MetricCard = ({ title, value, trend, trendValue, icon, color, description }) => (
    <motion.div
      className="bg-card border border-border rounded-2xl p-6 hover:shadow-elevation-2 transition-all duration-300"
      whileHover={{ y: -2 }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${color}/10`}>
          <Icon name={icon} size={24} className={`text-${color}`} />
        </div>
        <div className={`flex items-center space-x-1 text-sm font-medium ${
          trend === 'up' ? 'text-success' : 'text-destructive'
        }`}>
          <Icon name={trend === 'up' ? 'TrendingUp' : 'TrendingDown'} size={16} />
          <span>{trendValue}</span>
        </div>
      </div>
      <h3 className="text-2xl font-bold text-foreground mb-1">{value}</h3>
      <p className="text-sm font-medium text-foreground mb-1">{title}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </motion.div>
  );

  const ProfitChart = () => (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Tendências de Lucro</h3>
        <div className="flex space-x-2">
          <Button
            variant={selectedChart === 'profit-trends' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setSelectedChart('profit-trends')}
          >
            Lucro
          </Button>
          <Button
            variant={selectedChart === 'margin-trends' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setSelectedChart('margin-trends')}
          >
            Margem
          </Button>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        {selectedChart === 'profit-trends' ? (
          <AreaChart data={profitTrends}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="month" className="text-muted-foreground" />
            <YAxis className="text-muted-foreground" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
            />
            <Area type="monotone" dataKey="receita" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
            <Area type="monotone" dataKey="custos" stackId="2" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} />
            <Area type="monotone" dataKey="lucro" stackId="3" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
          </AreaChart>
        ) : (
          <LineChart data={profitTrends}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="month" className="text-muted-foreground" />
            <YAxis className="text-muted-foreground" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
            />
            <Line type="monotone" dataKey="margem" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }} />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );

  const CostBreakdownChart = () => (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Distribuição de Custos</h3>
        <Button variant="outline" size="sm" onClick={() => handleExportData('custos')}>
          <Icon name="Download" size={16} className="mr-2" />
          Exportar
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={costBreakdown}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={5}
              dataKey="value"
            >
              {costBreakdown?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry?.color} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value) => [`R$ ${value?.toFixed(2)}`, 'Valor']}
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-col justify-center space-y-3">
          {costBreakdown?.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: item?.color }}
                />
                <span className="text-sm font-medium text-foreground">{item?.name}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-foreground">R$ {item?.value?.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground">{item?.percentage}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const TopProjectsTable = () => (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Projetos Mais Lucrativos</h3>
        <Button variant="outline" size="sm" onClick={() => handleExportData('projetos')}>
          <Icon name="Download" size={16} className="mr-2" />
          Exportar
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Projeto</th>
              <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Receita</th>
              <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Custo</th>
              <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Lucro</th>
              <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Margem</th>
              <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Qtd</th>
            </tr>
          </thead>
          <tbody>
            {topProjects?.map((project, index) => (
              <motion.tr
                key={project?.id}
                className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <td className="py-4 px-2">
                  <div>
                    <div className="font-medium text-foreground text-sm">{project?.name}</div>
                    <div className="text-xs text-muted-foreground">{project?.material} • {project?.printTime}</div>
                  </div>
                </td>
                <td className="text-right py-4 px-2 text-sm font-medium text-foreground">{project?.revenue}</td>
                <td className="text-right py-4 px-2 text-sm text-muted-foreground">{project?.cost}</td>
                <td className="text-right py-4 px-2 text-sm font-semibold text-success">{project?.profit}</td>
                <td className="text-right py-4 px-2">
                  <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                    parseFloat(project?.margin?.replace('%', '')) > 60 
                      ? 'bg-success/10 text-success' : parseFloat(project?.margin?.replace('%', '')) > 50
                      ? 'bg-warning/10 text-warning' :'bg-secondary/10 text-secondary'
                  }`}>
                    {project?.margin}
                  </span>
                </td>
                <td className="text-center py-4 px-2 text-sm text-muted-foreground">{project?.quantity}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const MaterialEfficiencyChart = () => (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Eficiência por Material</h3>
        <div className="flex space-x-2">
          <select 
            className="px-3 py-1 text-sm border border-border rounded-md bg-background text-foreground"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e?.target?.value)}
          >
            <option value="week">Última Semana</option>
            <option value="month">Último Mês</option>
            <option value="quarter">Último Trimestre</option>
          </select>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={materialEfficiency}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis dataKey="material" className="text-muted-foreground" />
          <YAxis className="text-muted-foreground" />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px'
            }}
          />
          <Bar dataKey="profit" fill="#10b981" radius={[4, 4, 0, 0]} />
          <Bar dataKey="efficiency" fill="#3b82f6" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const CustomerSegments = () => (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Segmentos de Clientes</h3>
        <Button variant="outline" size="sm">
          <Icon name="Users" size={16} className="mr-2" />
          Ver Todos
        </Button>
      </div>
      <div className="space-y-4">
        {customerInsights?.map((segment, index) => (
          <motion.div
            key={index}
            className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-foreground">{segment?.segment}</h4>
                <span className="text-sm text-muted-foreground">{segment?.count} clientes</span>
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <span className="text-muted-foreground">Receita: <strong className="text-primary">R$ {segment?.revenue?.toFixed(2)}</strong></span>
                <span className="text-muted-foreground">Lucro: <strong className="text-success">R$ {segment?.profit?.toFixed(2)}</strong></span>
              </div>
            </div>
            <div className="ml-4">
              <div className="text-right">
                <div className="text-lg font-semibold text-foreground">
                  {((segment?.profit / segment?.revenue) * 100)?.toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground">Margem</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onMenuToggle={handleMobileMenuToggle}
        isMobileMenuOpen={mobileMenuOpen}
      />
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
        isMobileOpen={mobileMenuOpen}
        onMobileClose={handleMobileMenuClose}
      />
      <main 
        className={`
          pt-16 transition-all duration-300 ease-in-out
          lg:pt-16 sm:pt-14
          ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}
        `}
      >
        <div className="p-6 lg:p-6 sm:p-4">
          <Breadcrumb />
          
          {/* Header Section */}
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} className="text-success" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground lg:text-3xl sm:text-2xl">
                    Analytics de Lucro
                  </h1>
                  <p className="text-muted-foreground lg:text-base sm:text-sm">
                    Análise financeira completa do seu negócio de impressão 3D
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button variant="outline" onClick={() => handleExportData('completo')}>
                  <Icon name="Download" size={16} className="mr-2" />
                  Exportar Dados
                </Button>
                <Button variant="primary">
                  <Icon name="Plus" size={16} className="mr-2" />
                  Nova Análise
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Metrics Cards */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            {profitMetrics?.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </motion.div>

          {/* Main Analytics Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            {/* Left Column - Charts */}
            <div className="xl:col-span-2 space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <ProfitChart />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <MaterialEfficiencyChart />
              </motion.div>
            </div>

            {/* Right Column - Cost Breakdown */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <CostBreakdownChart />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
              >
                <CustomerSegments />
              </motion.div>
            </div>
          </div>

          {/* Bottom Section - Projects Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.6 }}
          >
            <TopProjectsTable />
          </motion.div>

          {/* Footer Summary */}
          <motion.div 
            className="mt-8 bg-gradient-to-r from-primary/5 to-success/5 border border-border rounded-xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.7 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-success mb-1">R$ 4.235,80</div>
                <div className="text-sm text-muted-foreground">Lucro Total (Mês)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">49,2%</div>
                <div className="text-sm text-muted-foreground">Margem Média</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-warning mb-1">267%</div>
                <div className="text-sm text-muted-foreground">ROI Médio</div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default ProfitAnalyticsDashboard;